import type { PlayerId } from '../../shared/types/ids';

/**
 * Puts a set of player ids back into roster order (R1-8, COOP-07).
 *
 * Every multi-player id array the platform hands out — the observed players on `ROUND_RESOLVED`,
 * a both-required moment's participants, the ids a scoring event contributes to, the players a
 * round's evidence is written for — is collected as children act, so its *order* is a record of
 * who acted first. Nothing downstream needs that, and a game scene, an analytics writer or a
 * parent surface reading it back can reconstruct exactly the head-to-head the non-competitive
 * invariant exists to make unrepresentable. Ordering by the roster instead means those arrays say
 * only *who*, which is all any of them are for.
 *
 * Lanes are fixed for a whole mission (MVP notes §6, COOP-08), so roster order is stable: it is
 * the same order the parent summary lists its rows in and the same order the resume guard matches
 * slots in. Duplicates collapse. An id that is not on the roster cannot arise from the runtime's
 * own paths, and is sorted rather than appended in arrival order — a deterministic tail is worth
 * more than preserving the order of something that should not be there.
 */
export function inRosterOrder(
  roster: readonly { readonly playerId: PlayerId }[],
  playerIds: Iterable<PlayerId>,
): PlayerId[] {
  const wanted = new Set(playerIds);

  const known: PlayerId[] = [];
  for (const player of roster) {
    if (wanted.delete(player.playerId)) known.push(player.playerId);
  }

  return [...known, ...[...wanted].sort()];
}
